import React from "react";
import { marketplaces } from "./constants";
import { applyDisplayPrice, formatDisplayPrice } from "../../utils/displayPrice";
import { getEbayStoreId } from "./helpers";

const ReviewStep = ({
  formData,
  processedImages,
  poshmarkConnected,
  vintedConnected,
  getImagesToSubmit,
  displayPricePercentage = 100,
  sizeChartFile,
  sizeChartRequired,
  ebayStores = [],
}) => {
  const displayPrice = (price) =>
    formatDisplayPrice(price, displayPricePercentage, { prefix: "$" });

  const selectedEbayStore = ebayStores.find(
    (store) =>
      String(getEbayStoreId(store)) ===
      String(formData.selectedEbayStoreId || ""),
  );

  return (
    <div className="step-panel">
      <div className="step-header">
        <h2>Review & Submit</h2>
        <p>Please review your product details before submitting</p>
      </div>
      <div className="review-sections">
        <div className="review-section">
          <h4>Product Images</h4>
          <div className="review-image">
            {getImagesToSubmit().map((img, index) => (
              <img key={index} src={img} alt={`Product ${index + 1}`} />
            ))}
            {/* Size Chart - show alongside product images if required */}
            {sizeChartRequired === "required" && sizeChartFile && (
              sizeChartFile.file.type.includes("pdf") ? (
                <a
                  href={sizeChartFile.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="size-chart-pdf"
                  style={{ display: "inline-block", margin: "10px", padding: "10px", background: "#f0f0f0", borderRadius: "4px", textDecoration: "none", color: "#333" }}
                >
                  Size Chart (PDF)
                </a>
              ) : (
                <img src={sizeChartFile.previewUrl} alt="Size Chart" />
              )
            )}
          </div>
        </div>
        <div className="review-section">
          <h4>Product Details</h4>
          <div className="review-grid">
            <div className="review-item">
              <strong>Title:</strong> {formData.title}
            </div>
            <div className="review-item">
              <strong>Description:</strong> {formData.description}
            </div>
            <div className="review-item">
              <strong>SKU:</strong> {formData.sku}
            </div>
            <div className="review-item">
              <strong>Price:</strong> {displayPrice(formData.price)}
            </div>
            <div className="review-item">
              <strong>Quantity:</strong> {formData.quantity}
            </div>
            {formData.brand && (
              <div className="review-item">
                <strong>Brand:</strong> {formData.brand}
              </div>
            )}
            {formData.category && (
              <div className="review-item">
                <strong>Category:</strong> {formData.category}
              </div>
            )}
          </div>
        </div>
        {formData.variants.some((v) => v.size || v.color || v.sku) && (
          <div className="review-section">
            <h4>Product Variants</h4>
            <div className="variants-review">
              {formData.variants.map((variant, index) => (
                <div key={variant.id} className="variant-review-item">
                  <strong>Variant {index + 1}:</strong>
                  {variant.size && <span>Size: {variant.size}</span>}
                  {variant.color && <span>Color: {variant.color}</span>}
                  {variant.sku && <span>SKU: {variant.sku}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="review-section">
          <h4>Selected Marketplaces</h4>
          <div className="selected-marketplaces">
            {formData.selectedMarketplaces.map((id) => {
              const marketplace = marketplaces.find((m) => m.id === id);
              if (!marketplace) return null;
              return (
                <div key={id} className="marketplace-preview">
                  <span className="marketplace-badge">{marketplace.name}</span>
                  <div className="marketplace-details">
                    {id === "shopify" && (
                      <div className="review-grid">
                        <div className="review-item">
                          <strong>Title:</strong> {formData.title}
                        </div>
                        <div className="review-item">
                          <strong>Description:</strong> {formData.description}
                        </div>
                        <div className="review-item">
                          <strong>Price:</strong> {displayPrice(formData.price)}
                        </div>
                        <div className="review-item">
                          <strong>SKU:</strong> {formData.sku}
                        </div>
                      </div>
                    )}
                    {id === "ebay" && (
                      <div className="review-grid">
                        <div className="review-item">
                          <strong>Title:</strong> {formData.title}
                        </div>
                        <div className="review-item">
                          <strong>SKU:</strong> {formData.sku}
                        </div>
                        <div className="review-item">
                          <strong>Price:</strong> {displayPrice(formData.price)}
                        </div>
                        {selectedEbayStore && (
                          <div className="review-item">
                            <strong>eBay Store:</strong>{" "}
                            {selectedEbayStore.storeName}
                            {selectedEbayStore.marketplace
                              ? ` (${selectedEbayStore.marketplace})`
                              : ""}
                          </div>
                        )}
                      </div>
                    )}
                    {id === "poshmark" && (
                      <div className="review-grid">
                        <div className="review-item">
                          <strong>Title:</strong> {formData.title}
                        </div>
                        <div className="review-item">
                          <strong>Description:</strong>{" "}
                          {formData.description?.substring(0, 50)}...
                        </div>
                        <div className="review-item">
                          <strong>Category:</strong>{" "}
                          {formData.poshmarkCategory || "Women"} &gt;{" "}
                          {formData.poshmarkSubCategory || "Dresses"}
                        </div>
                        <div className="review-item">
                          <strong>Brand:</strong> {formData.brand || "N/A"}
                        </div>
                        <div className="review-item">
                          <strong>Price:</strong> {displayPrice(formData.price)}
                          {formData.pricerange?.split(" - ")[1] && (
                            <small
                              style={{
                                marginLeft: "8px",
                                color: "#666",
                              }}
                            >
                              (Original: $
                              {applyDisplayPrice(
                                formData.pricerange?.split(" - ")[1],
                                displayPricePercentage,
                              )}
                              )
                            </small>
                          )}
                        </div>
                        <div className="review-item">
                          <strong>Size:</strong>{" "}
                          {formData.variants?.[0]?.size || "OS"} (
                          {formData.poshmarkSizeType || "standard"})
                        </div>
                        <div className="review-item">
                          <strong>Condition:</strong>{" "}
                          {formData.condition || "New With Tags"}
                        </div>
                        <div className="review-item">
                          <strong>Color:</strong>{" "}
                          {formData.color || "Not specified"}
                        </div>
                        <div
                          className="review-item"
                          style={{
                            color: poshmarkConnected ? "green" : "red",
                            fontWeight: "500",
                          }}
                        >
                          <strong>Extension:</strong>{" "}
                          {poshmarkConnected
                            ? "Connected"
                            : "Not Connected"}
                        </div>
                      </div>
                    )}
                    {id === "vinted" && (
                      <div className="review-grid">
                        <div className="review-item">
                          <strong>Title:</strong> {formData.title}
                        </div>
                        <div className="review-item">
                          <strong>Description:</strong>{" "}
                          {formData.description?.substring(0, 50)}...
                        </div>
                        <div className="review-item">
                          <strong>Category:</strong>{" "}
                          {formData.vintedCategoryPath.join(" > ") ||
                            formData.vintedCategory ||
                            "Not selected"}
                        </div>
                        <div className="review-item">
                          <strong>Brand:</strong> {formData.brand || "N/A"}
                        </div>
                        <div className="review-item">
                          <strong>Price:</strong> {displayPrice(formData.price)}
                        </div>
                        <div className="review-item">
                          <strong>Size:</strong>{" "}
                          {formData.vintedSize || "Not specified"}
                        </div>
                        <div className="review-item">
                          <strong>Package Size:</strong>{" "}
                          {formData.vintedPackageSize === "small"
                            ? "Small"
                            : formData.vintedPackageSize === "medium"
                              ? "Medium"
                              : "Large"}
                        </div>
                        <div className="review-item">
                          <strong>Condition:</strong>{" "}
                          {formData.condition || "Very good"}
                        </div>
                        <div className="review-item">
                          <strong>Color:</strong>{" "}
                          {formData.color || "Not specified"}
                        </div>
                        <div
                          className="review-item"
                          style={{
                            color: vintedConnected ? "green" : "red",
                            fontWeight: "500",
                          }}
                        >
                          <strong>Extension:</strong>{" "}
                          {vintedConnected ? "Connected" : "Not Connected"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;